import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import type {
  DemiJournee,
  QualificationPresence,
  SeanceAffichee,
  StatutPresence,
} from './useEmargement';

/**
 * Parcours d'émargement en salle (CDC §6.7).
 *
 * Les types reprennent ceux de `modules/emargement/session.service.ts` : la
 * forme rendue par l'API est la seule qui fasse foi, l'écran ne recalcule ni
 * l'effectif attendu, ni l'état d'un cycle, ni le verdict d'une journée.
 */

export type TypeJour =
  | 'CENTRE_FORMATION'
  | 'ENTREPRISE'
  | 'EPREUVE_ECRITE'
  | 'ECF'
  | 'FERIE'
  | 'VACANCES';

export interface VerdictJournee {
  emargeable: boolean;
  typeJour: TypeJour | null;
  libelle: string;
  raison: string | null;
}

export interface EtatDemiJournee {
  demiJournee: DemiJournee;
  libelle: string;
  seanceId: string | null;
  statut: 'PREVUE' | 'EN_COURS' | 'CLOTUREE' | null;
  appelValide: boolean;
  attendus: number;
  signes: number;
  absents: number;
  restants: number;
}

export interface SessionEmargeable {
  sessionId: string;
  session: string;
  formationCode: string;
  formationTitre: string;
  siteCode: string;
  siteNom: string;
  effectif: number;
  journee: VerdictJournee;
  demiJournees: EtatDemiJournee[];
  /** Code d'émargement paramétré qui s'appliquera, `null` s'il se choisit à l'appel. */
  codeParametre: 'SESSION' | 'FORMATION' | null;
}

export interface ResultatOuverture {
  seance: SeanceAffichee;
  journee: VerdictJournee;
  creee: boolean;
  ajoutes: number;
}

export interface ResultatAppel {
  seance: SeanceAffichee;
  aSigner: { presenceId: string; apprenantId: string; apprenant: string }[];
  message: string;
}

export interface LigneHistorique {
  seanceId: string;
  presenceId: string;
  date: string;
  demiJournee: DemiJournee;
  demiJourneeLibelle: string;
  formation: string | null;
  session: string | null;
  apprenantId: string;
  apprenant: string;
  statut: StatutPresence;
  qualification: QualificationPresence;
  qualificationLibelle: string;
  heureArrivee: string | null;
  heureDepart: string | null;
  minutesPresence: number | null;
  minutesPrevues: number;
  justifiee: boolean;
  motifJustification: string | null;
  signeLe: string | null;
  creeLe: string;
  modifieLe: string;
  appelPar: string | null;
  modifiePar: string | null;
}

export function useSessionsEmargeables(site: string, date: string, actif: boolean) {
  const parametres = new URLSearchParams({ site });
  if (date) parametres.set('date', date);

  return useQuery({
    queryKey: ['emargement-sessions', site, date],
    queryFn: () =>
      appelApi<{ jour: string; sessions: SessionEmargeable[] }>(
        `/emargement/sessions?${parametres.toString()}`,
      ),
    enabled: actif,
    // La salle émarge pendant que l'écran est ouvert : le suivi doit bouger
    // sans que le formateur ait à recharger.
    refetchInterval: 30_000,
  });
}

function invalider(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['emargement'] });
  void client.invalidateQueries({ queryKey: ['emargement-sessions'] });
  void client.invalidateQueries({ queryKey: ['emargement-historique'] });
  void client.invalidateQueries({ queryKey: ['absences'] });
}

export function useOuvrirDemiJournee() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entree: {
      sessionId: string;
      date: string;
      demiJournee: DemiJournee;
      heureDebut?: string;
      heureFin?: string;
      lieu?: string;
    }) => appelApi<ResultatOuverture>('/emargement/ouvrir', { methode: 'POST', corps: entree }),
    onSuccess: () => invalider(client),
  });
}

export function useValiderAppel() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      seanceId: string;
      pointages: {
        presenceId: string;
        statut: 'PRESENT' | 'RETARD' | 'ABSENT';
        heureArrivee?: string;
        justifiee?: boolean;
        motifJustification?: string;
      }[];
      pin?: string;
    }) => {
      const { seanceId, ...corps } = variables;
      return appelApi<ResultatAppel>(`/emargement/seances/${seanceId}/appel`, {
        methode: 'POST',
        corps,
      });
    },
    onSuccess: () => invalider(client),
  });
}

export function useRouvrirAppel() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { seanceId: string; pin?: string }) =>
      appelApi<SeanceAffichee>(`/emargement/seances/${variables.seanceId}/appel/reouverture`, {
        methode: 'POST',
        corps: { pin: variables.pin },
      }),
    onSuccess: () => invalider(client),
  });
}

/**
 * Sortie du mode signature. Le contrôle est fait par l'API : une vérification
 * gardée dans le navigateur se contournerait en rechargeant la page, et c'est
 * la tablette posée sur la table qu'il s'agit de protéger.
 */
export function useSortirModeSignature() {
  return useMutation({
    mutationFn: (variables: { seanceId: string; pin?: string }) =>
      appelApi<{ valide: true }>(`/emargement/seances/${variables.seanceId}/sortie`, {
        methode: 'POST',
        corps: { pin: variables.pin },
      }),
  });
}

export function useHistoriqueEmargement(
  filtres: { apprenantId?: string; sessionId?: string },
  actif: boolean,
) {
  const parametres = new URLSearchParams();
  if (filtres.apprenantId) parametres.set('apprenantId', filtres.apprenantId);
  if (filtres.sessionId) parametres.set('sessionId', filtres.sessionId);

  return useQuery({
    queryKey: ['emargement-historique', filtres.apprenantId ?? '', filtres.sessionId ?? ''],
    queryFn: () =>
      appelApi<{ lignes: LigneHistorique[] }>(
        `/emargement/historique?${parametres.toString()}`,
      ),
    enabled: actif && (Boolean(filtres.apprenantId) || Boolean(filtres.sessionId)),
  });
}
